import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Chrome } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, register, loginWithGoogle, checkAuth } = useAuth();
  const { toast } = useToast();
  const { t, language } = useLanguage();
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || '/members/dashboard';

  // Handle OAuth callback (for Safari redirect flow)
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const sessionId = searchParams.get('session_id');
      const oauthCallback = searchParams.get('oauth_callback');
      
      if (sessionId) {
        setLoading(true);
        try {
          // Exchange session_id for user data
          const response = await axios.get(`${API_URL}/auth/google/session`, {
            headers: { 'X-Session-ID': sessionId },
            withCredentials: true
          });
          
          // Refresh auth state
          await checkAuth();
          
          toast({ 
            title: language === 'es' ? "¡Bienvenido!" : "Welcome!", 
            description: language === 'es' ? "Sesión iniciada con Google exitosamente" : "Successfully logged in with Google" 
          });
          
          // Get stored return URL or use default
          const returnUrl = sessionStorage.getItem('oauth_return_url');
          sessionStorage.removeItem('oauth_return_url');
          
          navigate(returnUrl || from, { replace: true });
        } catch (error) {
          console.error('OAuth callback error:', error);
          toast({
            title: language === 'es' ? "Error de Google" : "Google Login Failed",
            description: language === 'es' ? "Por favor intente de nuevo" : "Please try again",
            variant: "destructive"
          });
        } finally {
          setLoading(false);
        }
      }
    };
    
    handleOAuthCallback();
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast({ 
        title: language === 'es' ? "¡Bienvenido de nuevo!" : "Welcome back!", 
        description: language === 'es' ? "Sesión iniciada exitosamente" : "Successfully logged in" 
      });
      navigate(from, { replace: true });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || (language === 'es' ? "Email o contraseña inválidos" : "Invalid email or password");
      
      // Check if it's an email verification error (403)
      if (error.response?.status === 403) {
        toast({
          title: language === 'es' ? "Correo no verificado" : "Email Not Verified",
          description: errorMessage,
          variant: "destructive"
        });
        // Redirect to check email page
        navigate('/check-email', { state: { email: loginData.email }, replace: true });
      } else {
        toast({
          title: language === 'es' ? "Error de inicio de sesión" : "Login Failed",
          description: errorMessage,
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/auth/register`,
        { email: registerData.email, name: registerData.name, password: registerData.password },
        { withCredentials: true }
      );
      
      // Check if verification is required
      if (response.data.requires_verification) {
        toast({ 
          title: language === 'es' ? "¡Cuenta creada!" : "Account Created!", 
          description: language === 'es' 
            ? "Revisa tu correo para verificar tu cuenta." 
            : "Check your email to verify your account." 
        });
        // Redirect to check email page
        navigate('/check-email', { state: { email: registerData.email }, replace: true });
      } else {
        // Old flow for backwards compatibility
        toast({ 
          title: language === 'es' ? "¡Cuenta creada!" : "Account Created!", 
          description: language === 'es' ? "Bienvenido a Exciting.Travel" : "Welcome to Exciting.Travel" 
        });
        navigate(from, { replace: true });
      }
    } catch (error) {
      toast({
        title: language === 'es' ? "Error de registro" : "Registration Failed",
        description: error.response?.data?.detail || (language === 'es' ? "Por favor intente de nuevo" : "Please try again"),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      await loginWithGoogle();
      toast({ 
        title: language === 'es' ? "¡Bienvenido!" : "Welcome!", 
        description: language === 'es' ? "Sesión iniciada con Google exitosamente" : "Successfully logged in with Google" 
      });
      navigate(from, { replace: true });
    } catch (error) {
      toast({
        title: language === 'es' ? "Error de Google" : "Google Login Failed",
        description: language === 'es' ? "Por favor intente de nuevo" : "Please try again",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center py-32 pt-48 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_75f4a617-7223-46ab-b5af-9fb0ce34d58c/artifacts/s7ixbz5d_customcolor_logo_transparent_background.png" 
            alt="Exciting.Travel" 
            className="h-20 w-auto mx-auto mb-6"
          />
          <h1 className="text-4xl font-black text-white mb-2">{t('login.title')}</h1>
          <p className="text-gray-400">{t('login.subtitle')}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="login" className="font-bold">{t('login.loginTab')}</TabsTrigger>
              <TabsTrigger value="register" className="font-bold">{t('login.signupTab')}</TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <Label htmlFor="login-email" className="font-bold mb-2 flex items-center space-x-2">
                    <Mail size={16} className="text-[#84bc00]" />
                    <span>{t('login.email')}</span>
                  </Label>
                  <Input
                    id="login-email"
                    type="email"
                    required
                    value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    className="border-2"
                    placeholder={language === 'es' ? 'tu@correo.com' : 'your@email.com'}
                  />
                </div>

                <div>
                  <Label htmlFor="login-password" className="font-bold mb-2 flex items-center space-x-2">
                    <Lock size={16} className="text-[#84bc00]" />
                    <span>{t('login.password')}</span>
                  </Label>
                  <Input
                    id="login-password"
                    type="password"
                    required
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    className="border-2"
                    placeholder="••••••••"
                  />
                  <div className="text-right mt-2">
                    <a 
                      href="/forgot-password" 
                      className="text-sm text-[#84bc00] hover:text-[#6a9600] font-semibold"
                    >
                      {language === 'es' ? '¿Olvidaste tu contraseña?' : 'Forgot password?'}
                    </a>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold py-6 text-lg"
                >
                  {loading ? t('login.loggingIn') : t('login.loginButton')}
                </Button>
              </form>

              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500 font-semibold">{t('login.or')}</span>
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  variant="outline"
                  className="w-full mt-6 border-2 font-bold py-6 text-lg hover:bg-gray-50"
                >
                  <Chrome size={20} className="mr-2" />
                  {t('login.continueGoogle')}
                </Button>
              </div>
            </TabsContent>

            {/* Register Tab */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-6">
                <div>
                  <Label htmlFor="register-name" className="font-bold mb-2 flex items-center space-x-2">
                    <User size={16} className="text-[#84bc00]" />
                    <span>{t('login.fullName')}</span>
                  </Label>
                  <Input
                    id="register-name"
                    type="text"
                    required
                    value={registerData.name}
                    onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                    className="border-2"
                    placeholder={language === 'es' ? 'Juan Pérez' : 'John Doe'}
                  />
                </div>

                <div>
                  <Label htmlFor="register-email" className="font-bold mb-2 flex items-center space-x-2">
                    <Mail size={16} className="text-[#84bc00]" />
                    <span>{t('login.email')}</span>
                  </Label>
                  <Input
                    id="register-email"
                    type="email"
                    required
                    value={registerData.email}
                    onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                    className="border-2"
                    placeholder={language === 'es' ? 'tu@correo.com' : 'your@email.com'}
                  />
                </div>

                <div>
                  <Label htmlFor="register-password" className="font-bold mb-2 flex items-center space-x-2">
                    <Lock size={16} className="text-[#84bc00]" />
                    <span>{t('login.password')}</span>
                  </Label>
                  <Input
                    id="register-password"
                    type="password"
                    required
                    value={registerData.password}
                    onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                    className="border-2"
                    placeholder="••••••••"
                    minLength={6}
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('login.minPassword')}</p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold py-6 text-lg"
                >
                  {loading ? t('login.creatingAccount') : t('login.createAccount')}
                </Button>
              </form>

              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500 font-semibold">{t('login.or')}</span>
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  variant="outline"
                  className="w-full mt-6 border-2 font-bold py-6 text-lg hover:bg-gray-50"
                >
                  <Chrome size={20} className="mr-2" />
                  {t('login.signupGoogle')}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Login;
